import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { ApiError, authApi, type User } from '../services/api/client';

export type LoginResult =
  | { twoFactorRequired: false }
  | { twoFactorRequired: true; challenge: string };

interface AuthState {
  user: User | null;
  /** True enquanto a sessao inicial ainda esta sendo verificada. */
  loading: boolean;
  /**
   * Resolve com `{ twoFactorRequired: true, challenge }` quando a conta tem
   * verificação em dois passos (PRD 39.2): a sessão ainda NÃO abriu, e a tela
   * precisa pedir o código e chamar `completeTwoFactor`.
   */
  login: (email: string, password: string) => Promise<LoginResult>;
  completeTwoFactor: (challenge: string, code: string) => Promise<void>;
  /** Retorna o id da turma em que o cadastro já matriculou (PRD 33.1). */
  register: (name: string, email: string, password: string, inviteToken: string) => Promise<string>;
  logout: () => Promise<void>;
  /** Reconsulta /auth/me — usado depois do redirect do login social (PRD
   * 33.5), onde a sessão foi criada pelo backend, não por uma chamada nossa. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Estado de autenticacao da aplicacao.
 *
 * O token vive num cookie httpOnly, entao o JavaScript nao consegue le-lo —
 * de proposito. A unica forma de saber quem esta logado e perguntar ao
 * backend em /auth/me, que e o que fazemos ao montar.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    authApi
      .me()
      .then((result) => {
        if (active) setUser(result.user);
      })
      .catch((error: unknown) => {
        // 401 aqui e o caso normal de "ninguem logado", nao um erro.
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.error('Falha ao verificar a sessao:', error);
        }
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const result = await authApi.login(email, password);

    if ('twoFactorRequired' in result) {
      return { twoFactorRequired: true, challenge: result.challenge };
    }

    setUser(result.user);
    return { twoFactorRequired: false };
  }, []);

  const completeTwoFactor = useCallback(async (challenge: string, code: string) => {
    const result = await authApi.loginTwoFactor(challenge, code);
    setUser(result.user);
  }, []);

  const register = useCallback(
    async (name: string, email: string, password: string, inviteToken: string) => {
      const result = await authApi.register(name, email, password, inviteToken);
      setUser(result.user);
      return result.classId;
    },
    [],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const result = await authApi.me();
      setUser(result.user);
    } catch {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, completeTwoFactor, register, logout, refresh }),
    [user, loading, login, completeTwoFactor, register, logout, refresh],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const context = use(AuthContext);

  if (!context) {
    throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  }

  return context;
}
